import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/axios';

const API_URL = '/api/calculator';

// ─── Leads ───────────────────────────────────────────────

export const fetchLeads = createAsyncThunk(
  'calculator/fetchLeads',
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const { leadsPagination } = getState().calculator;
      const queryParams = {
        page: leadsPagination.page,
        limit: leadsPagination.limit,
        ...params,
      };
      const response = await api.get(`${API_URL}/leads`, { params: queryParams });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ─── Settings ────────────────────────────────────────────

export const fetchSettings = createAsyncThunk(
  'calculator/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`${API_URL}/settings`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateSettings = createAsyncThunk(
  'calculator/updateSettings',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.put(`${API_URL}/settings`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ─── Import ──────────────────────────────────────────────

export const importXlsx = createAsyncThunk(
  'calculator/importXlsx',
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`${API_URL}/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ─── Slice ───────────────────────────────────────────────

const calculatorSlice = createSlice({
  name: 'calculator',
  initialState: {
    // Leads
    leads: [],
    leadsPagination: { page: 1, limit: 9999, total: 0, totalPages: 0 },
    leadsLoading: false,

    // Settings
    settings: [],        // [{key, value, description}, ...]
    auctionFees: [],     // raw rows from auction_fees table
    logistics: [],       // rows from logistics_matrix
    europeCosts: [],     // rows from europe_costs
    settingsLoading: false,
    saving: false,

    error: null,
  },
  reducers: {
    setLeadsPage: (state, action) => {
      state.leadsPagination.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchLeads
      .addCase(fetchLeads.pending, (state) => {
        state.leadsLoading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.leadsLoading = false;
        state.leads = action.payload.leads;
        state.leadsPagination = {
          ...state.leadsPagination,
          ...action.payload.pagination,
        };
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.leadsLoading = false;
        state.error = action.payload;
      })

      // fetchSettings
      .addCase(fetchSettings.pending, (state) => {
        state.settingsLoading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settings = action.payload.settings;
        state.auctionFees = action.payload.auction_fees;
        state.logistics = action.payload.logistics;
        state.europeCosts = action.payload.europe_costs;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.error = action.payload;
      })

      // updateSettings
      .addCase(updateSettings.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateSettings.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateSettings.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      // importXlsx
      .addCase(importXlsx.pending, (state) => {
        state.saving = true;
      })
      .addCase(importXlsx.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(importXlsx.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export const { setLeadsPage } = calculatorSlice.actions;
export default calculatorSlice.reducer;
